/**
 * Frontend mirror of the wizard's backend error contract.
 *
 * SOURCE: teamio-serverless-api/conversations-service/lib/waOnboarding/errors.js
 *         — `WIZARD_ERROR_CODES`, re-read and captured 2026-09-27 when `already_has_number` was added
 *         (my-purchase-number: one Teamio number per site) — 22 wizard-routable codes. Before that:
 *         2026-09-11, when the number picker added
 *         `number_taken` (my-purchase-number) and `invalid_area_code` + `number_search_unavailable`
 *         (my-number-options) — 21 wizard-routable codes. Before that: 2026-09-03, when
 *         `byo_ack_required` was added on the backend (18 wizard-routable codes). History: on 2026-09-02 the count went 18 → 17 as
 *         `quote_mismatch` was retired (Teamio pays for the number, the practice accepts no price, so
 *         there is nothing left on the wire that could mismatch a server quote); 2026-09-03 brought it
 *         back to 18 with `byo_ack_required`, the my-triage refusal that fires when a bring-your-own
 *         practice has not ticked every consequence of moving its existing number.
 *
 *         NOTE — the backend registry is LARGER than this mirror, on purpose. Since 2026-09-02 it also
 *         carries back-half refusal codes (waba_not_connected, no_general_phone, invalid_display_name,
 *         template_sid_missing, template_submit_failed, no_content_sid) that surface only to operators
 *         and the scheduler and are NEVER rendered in the wizard UI. This file mirrors only the
 *         practice-routable subset, which is why the snapshot in whatsapp-wizard-errors.test.js is that
 *         subset and not `Object.keys(registry)`.
 *
 * ── WHY A MIRROR AND NOT MESSAGE-MATCHING ────────────────────────────────────────────────────────
 * The backend registry exists because `my-triage` answers 409 for two unrelated reasons — "your
 * country is embargoed" and "you already started" — and the wizard renders a different screen for
 * each. Matching on the message TEXT breaks the first time someone improves the copy, so the wire
 * carries `{code, message}` and this file is the frontend half of that vocabulary.
 *
 * ── WHY THE MESSAGES ARE DUPLICATED HERE WHEN THE SERVER SENDS ONE ───────────────────────────────
 * `wizardErrorMessage` PREFERS the server's sentence, always: it is written for the specific
 * occurrence (the display-name error tells you how long yours actually was) and this file cannot
 * know that. The mirrored sentences are the floor, for the two cases where there is no server
 * sentence to show — a network failure, and a response whose body never arrived — plus a readable
 * reference for anyone working on this screen.
 *
 * ── WHAT THE MIRROR IS REALLY FOR ────────────────────────────────────────────────────────────────
 * Drift detection. `whatsapp-wizard-errors.test.js` pins this exact code SET against a hardcoded
 * snapshot, so a backend that adds, renames or removes a code fails loudly HERE — in a test — and
 * not silently in a practice's browser, where an unrecognized code would render the generic
 * "something went wrong" over a specific, actionable problem. When that test fails, re-read the
 * backend file, update this map AND the snapshot, and move the capture date above.
 */

/**
 * code → the customer-facing sentence to show when the server sent no message of its own.
 *
 * Deliberately not carrying `statusCode`: the REGISTRY owns the status code (backend header), the
 * frontend routes on the code alone, and a second copy of the numbers here would be a second thing
 * to keep in sync that nothing actually reads.
 */
export const WIZARD_ERROR_MESSAGES = {
  // ── Gate ──────────────────────────────────────────────────────────────────────────────────────
  wizard_not_available:
    'WhatsApp setup is not open for your practice yet. Contact Teamio and we will get you started.',

  // ── my-triage: answers that could never carry the setup forward ───────────────────────────────
  invalid_answers:
    'Your answers did not come through. Please go back through the questions and try again.',
  display_name_required:
    'Enter the name patients will see on your WhatsApp messages.',
  display_name_invalid:
    'That name is the wrong length. It must be between 3 and 512 characters — WhatsApp reviews it '
    + 'and patients see it on every message.',
  consent_required:
    'Please confirm you have permission to message these patients before continuing.',
  number_on_app_required:
    'Tell us whether that number is currently set up in the WhatsApp app.',
  byo_ack_required:
    'Please tick every box confirming you understand what changes when you move your existing '
    + 'WhatsApp number, then continue.',

  // ── my-triage: where this practice already is, and where it is ────────────────────────────────
  onboarding_in_progress:
    'WhatsApp setup has already been started for your practice. Refresh this page to pick up where '
    + 'it left off.',
  site_not_found:
    'We could not find your practice on your account. Please contact Teamio.',
  country_blocked:
    'WhatsApp messaging is not available in your country, so we are unable to set it up.',
  unknown_country:
    'We could not confirm which country your practice is in, so our team will take it from here.',

  // ── my-purchase-number: the endpoint that gets the practice a number ──────────────────────────
  // NO SENTENCE HERE MENTIONS A PRICE (2026-09-02). Teamio absorbs the cost of the number, so a
  // practice reading one of these has never been shown a figure — telling them a price could not be
  // confirmed would be explaining a step they never saw and implying a bill they will never get.
  // The server's own quote failures are internal and are described here by their EFFECT: no number
  // was set up, nothing changed.
  phone_number_required:
    'Pick a phone number first.',
  wrong_step:
    'Your setup has moved on since this page was loaded. Refresh to see where it is now.',
  not_purchase_path:
    'Based on your answers we do not set up a number at this step — our team takes over for an '
    + 'existing number or a local number outside the US.',
  quote_unavailable:
    'We could not set up a number just now. Please try again in a moment.',
  no_price_quote:
    'We could not set up a number just now, so nothing has changed. Our team will follow up.',
  purchase_failed:
    'We could not finish setting up your number. Please try again — our team is alerted if it '
    + 'keeps failing.',
  // 2026-09-11: the practice now PICKS from a list, so a number going between the list and the click
  // is ordinary. Nothing was bought; the list is re-read (NUMBER_TAKEN_CODES) and they pick again.
  number_taken:
    'That number was just taken by someone else, so nothing was bought. Please pick another one.',
  // 2026-09-27: one Teamio number per site. Nothing was bought; the screen re-reads status and swaps
  // the picker for the contact-us panel (REFRESH_ERROR_CODES).
  already_has_number:
    'Your practice already has a Teamio number, so we can\'t set up another one here. If you need a '
    + 'second number, contact Teamio and we\'ll help.',

  // ── my-number-options: the number screen's lists (2026-09-11) ────────────────────────────────
  invalid_area_code:
    'Enter a three-digit US area code, like 561.',
  number_search_unavailable:
    'We could not look up numbers just now. Please try again in a moment.',

  // ── my-handoff ────────────────────────────────────────────────────────────────────────────────
  invalid_reason:
    'We could not pass this to our team. Please refresh and try again.',
};

/** Every code in the contract, in registry order. The node-test pins this set. */
export const WIZARD_ERROR_CODES = Object.keys(WIZARD_ERROR_MESSAGES);

/**
 * The last-resort sentence. Shown for a code this build has never heard of (backend ahead of
 * frontend) and for a failure that produced no code at all (network, gateway, 500).
 */
export const GENERIC_WIZARD_ERROR =
  'Something went wrong and your last change was not saved. Please try again — if it keeps '
  + 'happening, contact Teamio.';

/**
 * Codes whose right answer is a person, not a retry.
 *
 * `unknown_country` is raised BEFORE anything is written, so the practice is still at the very
 * start and a retry would fail identically — the only way forward is our team. Kept as a named set
 * so the routing reads as a rule rather than as a string comparison buried in a component.
 */
export const HANDOFF_ERROR_CODES = ['unknown_country'];

/** Codes that mean this practice can never run the wizard, so the screen must change, not retry. */
export const TERMINAL_ERROR_CODES = ['country_blocked'];

/**
 * Codes that mean THE SCREEN IS OUT OF DATE — re-read status, and never auto-retry.
 *
 * Each of these says the server's view of this site has moved on from what the browser is showing:
 * setup was already started, the site is no longer at the step this action belongs to, or the
 * answers put it on a different branch. Retrying is wrong for every one of them; the right answer is
 * always the same — show the message, re-read `my-status`, let the wizard re-render, and let the
 * practice decide again on what is now true.
 *
 * ── TWO CODES LEFT THIS LIST ON 2026-09-02 ───────────────────────────────────────────────────────
 * `quote_mismatch` was retired outright (no accepted price exists to mismatch). `no_price_quote` was
 * removed for a subtler reason worth writing down: it was added here because the browser could be
 * left "sitting on a rendered price the server no longer trusts, with a live Buy button over it",
 * and only a re-read could replace that figure. There IS no rendered figure any more — Teamio pays
 * for the number and no price is on the wire — so that reasoning expired with the price panel.
 * Worse, a refresh here would now be actively wrong: the status watcher clears
 * `numberSetupUnavailable` on every new status, so re-reading would blank the "we can't set up a
 * number right now" panel the refusal just raised. NUMBER_SETUP_UNAVAILABLE_CODES handles it
 * instead, and the practice's own Refresh button is what tries again.
 */
export const REFRESH_ERROR_CODES = [
  'onboarding_in_progress',
  'wrong_step',
  'not_purchase_path',
  // 2026-09-27: the site has a number this screen did not know about — the re-read brings it in.
  'already_has_number',
];

/**
 * Codes meaning WE COULD NOT PROVISION A NUMBER JUST NOW.
 *
 * Both are the server's internal price check failing — Twilio unreachable, or a quote it will not
 * buy against. To the practice they are one thing: no number was set up and nothing changed. The
 * number screen swaps its form for an explanation when one of these comes back.
 *
 * This exists because the screen lost its old signal. It used to infer the same state from the
 * ABSENCE of a price quote in my-status; that field was removed on 2026-09-02, so the server's
 * refusal is now the only honest evidence, and it only exists AFTER an attempt.
 */
export const NUMBER_SETUP_UNAVAILABLE_CODES = ['quote_unavailable', 'no_price_quote'];

/**
 * Codes meaning THE LIST ON SCREEN IS STALE — the picked number is gone (2026-09-11).
 *
 * Not a status refresh (the site's step did not move) and not the can't-set-up panel (setup works,
 * that one number just went). The number screen's lists are re-read instead, so the practice picks
 * from numbers that exist.
 */
export const NUMBER_TAKEN_CODES = ['number_taken'];

/**
 * Should this failure be followed by a status re-read?
 *
 * Takes a `parseWizardError` result rather than a bare code so terminal refusals are covered by the
 * same call: an embargoed country is persisted server-side, so re-reading is what actually swaps
 * the form for the blocked screen.
 */
export function shouldRefreshStatus(parsed) {
  if (!parsed || typeof parsed !== 'object') return false;
  if (parsed.terminal === true) return true;
  return typeof parsed.code === 'string' && REFRESH_ERROR_CODES.includes(parsed.code);
}

/** True when this build recognizes the code. False for `undefined` and for a backend addition. */
export function isKnownWizardError(code) {
  return typeof code === 'string'
    && Object.prototype.hasOwnProperty.call(WIZARD_ERROR_MESSAGES, code);
}

/**
 * The sentence to show the practice.
 *
 * Order is deliberate: the server's own message first (it is occurrence-specific), the mirrored
 * message next (this build knows the code but got no text), the generic last.
 */
export function wizardErrorMessage(code, serverMessage) {
  if (typeof serverMessage === 'string' && serverMessage.trim() !== '') return serverMessage.trim();
  if (isKnownWizardError(code)) return WIZARD_ERROR_MESSAGES[code];
  return GENERIC_WIZARD_ERROR;
}

/**
 * Normalize an axios failure into `{ code, message, handoff, terminal }`.
 *
 * Defensive by contract, like the rest of this surface: a gateway 502 with an HTML body, a network
 * error with no `response` at all, and a JSON body missing `code` all have to produce something
 * renderable. Never throws.
 */
export function parseWizardError(err) {
  const body = (err && err.response && err.response.data) || null;
  const code = body && typeof body.code === 'string' ? body.code : null;
  const serverMessage = body && typeof body.message === 'string' ? body.message : null;
  return {
    code,
    message: wizardErrorMessage(code, serverMessage),
    // `handoff: true` also rides on the wire for unknown_country; trust either signal.
    handoff: Boolean((body && body.handoff === true) || (code && HANDOFF_ERROR_CODES.includes(code))),
    terminal: Boolean(code && TERMINAL_ERROR_CODES.includes(code)),
  };
}
